// The example story of the hero timeline (owner decision 2026-10-14): an outage
// starts at 0:40. Uptrack checks every 30 s and alerts at the first failed check
// (1:00); the site is fixed at 4:00. A 5 minute plan checks at 0:00 and 5:00 and
// alerts at 5:00, while customers still cannot reach the site. The times are an
// example and the page says so. Pure: no DOM, no clock.

/** The axis spans 5 minutes. */
export const SCALE_S = 300
export const OUTAGE_S = 40
export const UPTRACK_EVERY_S = 30
export const OTHERS_EVERY_S = 300
/** The first Uptrack check after the outage; the example alerts right away. */
export const ALERT_S = 60
export const FIXED_S = 240

/** Wait before the sweep, the sweep (5 minutes of the axis), the still finished picture, the fade. */
export const START_MS = 600
export const SWEEP_MS = 14000
export const HOLD_MS = 5000
export const FADE_MS = 600
export const LOOP_MS = START_MS + SWEEP_MS + 250 + HOLD_MS + FADE_MS

const US_CHECKS = SCALE_S / UPTRACK_EVERY_S + 1 // 0:00 to 5:00 every 30 s = 11
const OTHERS_CHECKS = SCALE_S / OTHERS_EVERY_S + 1 // 0:00 and 5:00 = 2
const EPS = 1e-6

/** Position on the axis, in percent of its width. */
export const pct = (seconds: number) => (seconds / SCALE_S) * 100

export type Frame = {
  /** The sweep has started. */
  running: boolean
  /** Time moves (the people walk). */
  moving: boolean
  /** Seconds on the axis, 0 to 300. */
  t: number
  usChecks: number
  othersChecks: number
  /** The Uptrack alert has come (1:00). */
  know: boolean
  /** The site is fixed in the Uptrack row (4:00). */
  fixed: boolean
  /** The 5 minute plan has alerted (5:00). */
  othersKnow: boolean
  /** The finished picture fades out before the next loop. */
  fading: boolean
}

export function frameAt(ms: number): Frame {
  const m = ((ms % LOOP_MS) + LOOP_MS) % LOOP_MS
  const running = m >= START_MS
  const frac = Math.min(1, Math.max(0, (m - START_MS) / SWEEP_MS))
  const t = frac * SCALE_S
  const count = (every: number, max: number) => (running ? Math.min(max, Math.floor(t / every + EPS) + 1) : 0)
  return {
    running,
    moving: running && frac < 1,
    t,
    usChecks: count(UPTRACK_EVERY_S, US_CHECKS),
    othersChecks: count(OTHERS_EVERY_S, OTHERS_CHECKS),
    know: running && t >= ALERT_S - EPS,
    fixed: running && t >= FIXED_S - EPS,
    othersKnow: running && frac >= 1,
    fading: m > LOOP_MS - FADE_MS,
  }
}

/** The still picture for reduced motion: the end of the sweep. */
export const FINAL: Frame = frameAt(START_MS + SWEEP_MS + 400)

/** Uptrack check `i` (0 to 10) finds the site down from the first check after the outage until the fix. */
export const usCheckDown = (i: number) => {
  const s = i * UPTRACK_EVERY_S
  return s >= ALERT_S && s < FIXED_S
}

/** The 5 minute plan: the 0:00 check finds the site up, the 5:00 check finds it still down. */
export const othersCheckDown = (i: number) => i >= 1

/** "20 s", "2 min", "4 min 20 s". */
export function formatSpan(seconds: number) {
  const min = Math.floor(seconds / 60)
  const sec = Math.round(seconds % 60)
  if (min === 0) return `${sec} s`
  return sec === 0 ? `${min} min` : `${min} min ${sec} s`
}

export const US_CHECK_COUNT = US_CHECKS
export const OTHERS_CHECK_COUNT = OTHERS_CHECKS
